import React, { useState, useEffect, useMemo } from "react";
import { Card, CardBody, CardTitle, Form, FormGroup, Label, Input, Row, Col, Button } from "reactstrap";
import Select from "react-select";
import { stringifyJSON } from "../../_utils/generalUtils";

const uomOptions = [
  { value: "LS", label: "LS" },
  { value: "LF", label: "LF" },
  { value: "CY", label: "CY" },
  { value: "CF", label: "CF" },
  { value: "SQYD", label: "SQYD" },
  { value: "SQFT", label: "SQFT" },
  { value: "LB", label: "LB" },
  { value: "EA", label: "EA" },
  { value: "MFBM", label: "MFBM" },
  { value: "TON", label: "TON" },
  { value: "WDAY", label: "WDAY" },
];

const BidItemCard = ({ itemNo, items, projItemList, onValidate, projectId, activeTab }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [editableFields, setEditableFields] = useState({ description: "", eeQuantity: "", unitOfMeasure: "" });
  const [action, setAction] = useState("upload");
  const [selectedMergeItem, setSelectedMergeItem] = useState(itemNo);

  const selectedUom = useMemo(() =>
    uomOptions.find(opt => opt.value === editableFields.unitOfMeasure), [editableFields.unitOfMeasure]);

  useEffect(() => {
    if (items.length > 0) {
      const firstItem = items[0];
      setSelectedItem(firstItem);
      setEditableFields(
        {
          description: firstItem.description,
          eeQuantity: firstItem.eeQuantity,
          unitOfMeasure: firstItem.unitOfMeasure
        }
      );
    }

   const existingBidItem = projItemList.find(item => item.itemNo === parseInt(itemNo));
    setAction(existingBidItem ? "merge" : "upload");
    setSelectedMergeItem(existingBidItem?.itemNo || projItemList[0]?.itemNo || null);
  }, [items, projItemList, itemNo]);

  const handleFieldChange = (e) => {
    setEditableFields(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };


  const handleSelectionChange = (e) => {
    const item = items.find(item => item.bidItemGuid === e.target.value);
    if (!item) return;
    setSelectedItem(item);
    setEditableFields({ description: item.description, eeQuantity: item.eeQuantity, unitOfMeasure: item.unitOfMeasure });
  };

  const handleUomChange = (selected) => {
    setEditableFields(prev => ({ ...prev, unitOfMeasure: selected.value }));
  };

  const handleValidate = () => {
    if (action === "upload" && (!editableFields.description || !editableFields.eeQuantity || !editableFields.unitOfMeasure)) {
      alert("All fields must be filled out for upload into project data");
      return;
    }

    let updatedBidItems = [...items.map(item => ({ ...item, uploadedBy: undefined }))];
    let selectedBidItemGuid = null;

    if (action === "upload") {
      const updatedItem = { ...selectedItem, ...editableFields, uploadedBy: undefined };
      updatedBidItems = updatedBidItems.map(item => (item.bidItemGuid === selectedItem.bidItemGuid ? updatedItem : item));
      selectedBidItemGuid = updatedItem.bidItemGuid;
    } else {
      const matchedItem = projItemList.find(item => item.itemNo === parseInt(selectedMergeItem));
      if (!matchedItem) return;
      updatedBidItems.push({ ...items[0], ...matchedItem, uploadedBy: undefined });
      selectedBidItemGuid = matchedItem.bidItemGuid;
    }

    onValidate(
      projectId,
      {
        BidItems: updatedBidItems,
        Ccos: [],
        Locations: [],
        SelectedBidItemGuid: selectedBidItemGuid,
        SelectedCcoGuid: null,
        SelectedLocationGuid: null
      },
      activeTab);
  };

  return (
    <Card style={{ marginBottom: "1rem" }}>
      <CardBody>
        <CardTitle tag="h5">Item #{itemNo}</CardTitle>
        <Form>
          <FormGroup>
            <Label for={`select-${itemNo}`}>Select</Label>
            <Input
              type="select"
              id={`select-${itemNo}`}
              value={selectedItem?.bidItemGuid || ""}
              onChange={handleSelectionChange }
            >
              {items.filter((item) => !item.isValidated).map((item) => (
                <option key={item.bidItemGuid} value={item.bidItemGuid}>
                  From {item.uploadedBy} on {new Date(item.uploadedOn).toLocaleString()}
                </option>
              ))}
            </Input>
          </FormGroup>
          {selectedItem && (
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for={`description-${itemNo}`}>Description</Label>
                  <Input
                    type="text"
                    id={`description-${itemNo}`}
                    name="description"
                    value={editableFields.description}
                    onChange={handleFieldChange}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for={`eeQuantity-${itemNo}`}>Bid QTY</Label>
                  <Input
                    type="text"
                    id={`eeQuantity-${itemNo}`}
                    name="eeQuantity"
                    value={editableFields.eeQuantity}
                    onChange={handleFieldChange}
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for={`unitOfMeasure-${itemNo}`}>UoM</Label>
                  <Select
                    id={`unitOfMeasure-${itemNo}`}
                    value={selectedUom}
                    options={uomOptions}
                    onChange={handleUomChange}
                    isSearchable={false}
                    placeholder="Select UoM"
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
          <FormGroup tag="fieldset" className="mt-4">
            <legend>Action</legend>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  name="action"
                  value="upload"
                  checked={action === "upload"}
                  onChange={() => setAction("upload")}
                  disabled={projItemList.some((item) => item.itemNo === parseInt(itemNo))}
                />{" "}
                Upload Selected Item
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  name="action"
                  value="merge"
                  checked={action === "merge"}
                  onChange={() => setAction("merge")}
                  disabled={projItemList.length === 0}
                />{" "}
                Merge with Existing Item
              </Label>
              <Input
                type="select"
                id={`mergeWith-${itemNo}`}
                value={selectedMergeItem}
                onChange={(e) => setSelectedMergeItem(e.target.value)}
                disabled={action !== "merge"}
              >
                {projItemList.map((item) => (
                  <option key={item.itemNo} value={item.itemNo}>
                    #{item.itemNo} - {item.description} ({item.eeQuantity} {item.unitOfMeasure})
                  </option>
                ))}
              </Input>
            </FormGroup>
          </FormGroup>
          <Button
            color="primary"
            onClick={handleValidate}
            className="mt-3"
          >
            Validate
          </Button>
        </Form>
      </CardBody>
    </Card>
  );
};

export default BidItemCard;
